import React from 'react';
import {
  compose,
  setDisplayName,
  withProps,
} from 'recompose';
import { FormattedNumber, FormattedMessage, injectIntl } from 'react-intl';
import { StatefulAnchor } from '@ux/dom';
import Badge, { Kind as BadgeKind } from '@ux/badge';
import BundlePurchase, { Against } from './Purchase';
import { withBreakpoints } from '@ux/responsive';
import { withStyles, percentage as formatPercent } from '@ux/style-utils';
import { isTrue, nuller, hasLength, percentage } from '@team-griffin/capra';
import stylesheet from './Bundle.stylesheet';
import * as r from 'ramda';

export const PureBundle = ({
  theme,
  domains,
  options,
  currency,
  availability,
  styles,
  showLink,
  price,
  oldPrice,
  saving,
  onViewBasketClick,
  onPurchaseBundleClick,
}) => (
  <div style={styles.root}>
    <div style={styles.body}>
      <div style={styles.promo}>
        <Badge
          theme={theme}
          kind={BadgeKind.HIGHLIGHT}
        >
          <FormattedMessage id="dst.exactMatch.bundles.promo"/>
        </Badge>
        <span style={styles.saving}>
          <FormattedMessage
            id="dst.exactMatch.bundles.saving"
            values={{
              saving: formatPercent(saving),
            }}
          />
        </span>
      </div>
      <p style={styles.text}>
        <FormattedMessage
          id="dst.exactMatch.bundles.text"
          values={{
            link: r.ifElse(
              isTrue,
              () => (
                <StatefulAnchor
                  href={options.url}
                  style={styles.link}
                >
                  <FormattedMessage id="dst.exactMatch.bundles.text.link"/>
                </StatefulAnchor>
              ),
              nuller,
            )(showLink),
          }}
        />
      </p>
      <ul
        style={styles.domains}
        data-test-id="bundle-domains"
      >
        <For
          each="domain"
          of={domains}
        >
          <li
            style={styles.domain}
            key={domain.fqdn}
          >
            {domain.fqdn}
          </li>
        </For>
      </ul>
    </div>

    <div style={styles.action}>
      <p style={styles.cost}>
        <span style={styles.priceText}>
          <FormattedMessage
            id="dst.exactMatch.bundles.price"
            values={{
              price: (
                <span style={styles.price}>
                  <FormattedNumber
                  // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency={currency}
                    value={price}
                  />
                </span>
              ),
            }}
          />
        </span>
        <span style={styles.oldPrice}>
          <FormattedNumber
            // eslint-disable-next-line react/style-prop-object
            style="currency"
            currency={currency}
            value={oldPrice}
          />
        </span>
      </p>
      <div style={styles.purchase}>
        <BundlePurchase
          theme={theme}
          onPurchaseDomainClick={onPurchaseBundleClick}
          onViewBasketClick={onViewBasketClick}
          intlNamespace={'exactMatch.bundles'}
          status={availability}
          against={Against.LIGHT}
        />
      </div>
    </div>
  </div>
);


const getTotal = (key, domains) => r.pipe(
  r.defaultTo([]),
  r.reduce(
    (total, domain) => r.add(total, r.propOr(0, key, domain)),
    0,
  ),
)(domains);

const enhance = compose(
  setDisplayName('ExactMatchBundle'),
  injectIntl,
  withProps((ownerProps) => {
    const {
      domains,
    } = ownerProps;

    const oldPrice = getTotal('oldPrice', domains);
    const price = getTotal('price', domains);
    const difference = r.subtract(oldPrice, price);
    const saving = Math.ceil(percentage(difference, oldPrice));
    const showLink = r.pathSatisfies(
      hasLength,
      [ 'options', 'url' ],
      ownerProps
    );

    return {
      oldPrice,
      price,
      saving,
      showLink,
    };
  }),
  withBreakpoints,
  withStyles(stylesheet),
);

export default enhance(PureBundle);
